import React from 'react';
import * as S from '../claim-timeline.styles';
import * as Card from './timeline-detail-card.styles';
import { IHL7DetailProps } from './hl7-detail.types';
import { ScrollableCodeContainer } from '../claim-timeline.styles';
import JsonView from '@uiw/react-json-view';
import { PayloadComparisonViewer } from '../../../payload-comparison-viewer';
import { formatDate } from '../../../../services/primitives/date-utils';
import { BaseButton } from '../../../..';

const safeParseJson = (text: string): unknown | null => {
    try {
        return JSON.parse(text);
    } catch {
        return null;
    }
};

const removeNullObjectFields = (value: unknown): unknown => {
    if (Array.isArray(value)) {
        return value.map(item => removeNullObjectFields(item));
    }
    if (value && typeof value === 'object') {
        const record = value as Record<string, unknown>;
        return Object.keys(record).reduce<Record<string, unknown>>((acc, key) => {
            const normalized = removeNullObjectFields(record[key]);
            if (normalized !== null) {
                acc[key] = normalized;
            }
            return acc;
        }, {});
    }
    return value;
};

type JsonViewExpandMode = 'default' | 'all' | 'none';

const ExpandableJsonView: React.FC<{ value: object }> = ({ value }) => {
    const [expandMode, setExpandMode] = React.useState<JsonViewExpandMode>('default');
    const [viewKey, setViewKey] = React.useState(0);

    const handleExpandMode = (mode: JsonViewExpandMode) => {
        setExpandMode(mode);
        setViewKey(current => current + 1);
    };

    const collapsed = expandMode === 'none';
    const shouldExpandNodeInitially =
        expandMode === 'all'
            ? () => true
            : expandMode === 'default'
              ? (_isExpanded: boolean, { level }: { level: number }) => level < 2
              : undefined;

    return (
        <div>
            <S.JsonViewToolbar>
                <BaseButton variant="text" size="small" type="button" onClick={() => handleExpandMode('all')}>
                    Expand all
                </BaseButton>
                <BaseButton variant="text" size="small" type="button" onClick={() => handleExpandMode('none')}>
                    Collapse all
                </BaseButton>
            </S.JsonViewToolbar>
            <JsonView
                key={viewKey}
                value={value}
                displayDataTypes={false}
                displayObjectSize={false}
                enableClipboard={true}
                collapsed={collapsed}
                {...(shouldExpandNodeInitially ? { shouldExpandNodeInitially } : {})}
            />
        </div>
    );
};

const renderJsonOrText = (text: string): React.ReactElement => {
    const parsed = safeParseJson(text);
    if (parsed !== null) {
        const normalized = removeNullObjectFields(parsed);
        return <ExpandableJsonView value={normalized as object} />;
    }

    return (
        <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '12px' }}>
            {text}
        </pre>
    );
};

function getHL7JSONPayload(hl7: any): string | undefined {
    let hl7Payload: string | undefined = hl7.message;

    if (hl7.supportProfessional) {
        hl7Payload = JSON.stringify(hl7.supportProfessional, null, 4);
    } else if (hl7.providerProfessional) {
        hl7Payload = JSON.stringify(hl7.providerProfessional, null, 4);
    } else if (hl7.supportInstitutional) {
        hl7Payload = JSON.stringify(hl7.supportInstitutional, null, 4);
    } else if (hl7.providerInstitutional) {
        hl7Payload = JSON.stringify(hl7.providerInstitutional, null, 4);
    } else if ('professional' in hl7 && hl7.professional) {
        hl7Payload = JSON.stringify(hl7.professional, null, 4);
    } else if ('institutional' in hl7 && hl7.institutional) {
        hl7Payload = JSON.stringify(hl7.institutional, null, 4);
    }

    return hl7Payload;
}

function hasJSONPayload(hl7: any): boolean {
    return (
        !!hl7.supportProfessional ||
        !!hl7.providerProfessional ||
        !!hl7.supportInstitutional ||
        !!hl7.providerInstitutional ||
        ('professional' in hl7 && !!hl7.professional) ||
        ('institutional' in hl7 && !!hl7.institutional)
    );
}

/**
 * HL7Detail Component
 *
 * Displays detailed information about an HL7 message.
 * Supports both Provider and Support app-specific display requirements.
 *
 * Provider: Shows ID, Type, Created, and Payload (message field only)
 * Support: Shows ID, Type, Created, Request (message), Response, and claim payload
 *
 * Used by both Provider and Support apps in their timeline views.
 */
export const HL7Detail: React.FunctionComponent<IHL7DetailProps> = (props: IHL7DetailProps): React.ReactElement => {
    const { hl7Message, appType, before } = props;
    const { formatted: afterMessage } = React.useMemo(() => {
        const raw = hl7Message.message || '';
        const parsed = safeParseJson(raw);
        const formatted = (parsed ? JSON.stringify(parsed, null, 2) : raw).replace(/\r|\n/g, '\n');
        return { formatted };
    }, [hl7Message.message]);
    const { formatted: beforeMessage } = React.useMemo(() => {
        const raw = before?.message || '';
        const parsed = safeParseJson(raw);
        const formatted = (parsed ? JSON.stringify(parsed, null, 2) : raw).replace(/\r|\n/g, '\n');
        return { formatted };
    }, [before?.message]);
    const showDiff = !!before;
    const renderPayloadDiff = () => {
        if (!before) {
            return <ScrollableCodeContainer>{renderJsonOrText(afterMessage)}</ScrollableCodeContainer>;
        }

        return (
            <PayloadComparisonViewer
                currentX12={afterMessage}
                previousX12={beforeMessage}
                showDiff={showDiff}
                previousLabel="Before"
                currentLabel="After"
                stripNullFields={true}
            />
        );
    };

    const renderCreated = (): string => {
        if (!hl7Message.created) {
            return '—';
        }

        try {
            return formatDate(hl7Message.created, 'MMM dd, yyyy hh:mm a');
        } catch (error) {
            return '—';
        }
    };

    const requestMessage = (hl7Message.message || '').replace(/\r|\n/g, '\n');
    const responseMessage = (hl7Message.response || '').replace(/\r|\n/g, '\n');

    return (
        <Card.CdPanel $embedded>
            <Card.CdSectionCard $embedded>
                <S.ExpandTable>
                    <S.ExpandTBody>
                        <S.ExpandTR>
                            <S.ExpandTH>ID</S.ExpandTH>
                            <S.ExpandTD>{hl7Message.id || '—'}</S.ExpandTD>
                        </S.ExpandTR>
                        {hl7Message.type && (
                            <S.ExpandTR>
                                <S.ExpandTH>Type</S.ExpandTH>
                                <S.ExpandTD>{hl7Message.type}</S.ExpandTD>
                            </S.ExpandTR>
                        )}
                        <S.ExpandTR>
                            <S.ExpandTH>Created</S.ExpandTH>
                            <S.ExpandTD>{renderCreated()}</S.ExpandTD>
                        </S.ExpandTR>
                    </S.ExpandTBody>
                </S.ExpandTable>
            </Card.CdSectionCard>

            {requestMessage && (
                <Card.CdSectionCard $embedded>
                    <S.ExpandLabel>Request</S.ExpandLabel>
                    <ScrollableCodeContainer>{renderJsonOrText(requestMessage)}</ScrollableCodeContainer>
                </Card.CdSectionCard>
            )}

            {appType === 'support' && !hl7Message.response && (
                <Card.CdSectionCard $embedded>
                    <S.ExpandTable>
                        <S.ExpandTBody>
                            <S.ExpandTR>
                                <S.ExpandTD>No response received for this HL7 message</S.ExpandTD>
                            </S.ExpandTR>
                        </S.ExpandTBody>
                    </S.ExpandTable>
                </Card.CdSectionCard>
            )}

            {appType === 'support' && !!hl7Message.response && (
                <Card.CdSectionCard $embedded>
                    <S.ExpandLabel>Response</S.ExpandLabel>
                    {renderJsonOrText(responseMessage)}
                </Card.CdSectionCard>
            )}

            {showDiff && (
                <Card.CdSectionCard $embedded>
                    <S.ExpandLabel>Payload</S.ExpandLabel>
                    {renderPayloadDiff()}
                </Card.CdSectionCard>
            )}
        </Card.CdPanel>
    );
};
